// Quick view, a new character or a new place (`kind`): its name, and a blank panel to say what it looks like. "Draw
// them" ("Draw it" for a place) fills the panel with the drawing, talking face and all. "Use them" ("Use it") takes it
// back to the picker; "Try again" goes back to the words, to change them and draw again. A place has no face, so it
// has no face placer. The asset is a draft (src/library/drafts.ts) until its picture arrives, and thrown away if the
// page is left before then. Drawing keeps going if the page is left and it lands in the library. Characters always
// record their own voice here. It paints in the style it is given (the episode's), else the last one used.
import { useEffect, useRef, useState } from "react";
import { changeAsset, dropDraft, getDraft, latestAsset, saveNow, useAsset, useSaveError } from "../../library/drafts";
import { hasPicture } from "../../library/build";
import { paintLook } from "../../library/paintLook";
import { findAsset } from "../../model/library";
import { getLibrary } from "../../library/store";
import { AssetView, Body, DoneFooter, Footer, Icon, Layer, Page, Toast, TopBar } from "../common";
import { FaceStage } from "../library/AssetPage";
import { lastStyleId, rememberStyle, styleById, useStyles } from "../images/styles";
import { isRunning, lookJob, notify, runJob, useJob } from "../jobs";
import { Drawing, Panel, Problem, paintProblem } from "./Panel";

export function NewCharacter({ assetId, kind = "character", style: given, onBack, onUse }: { assetId: string; kind?: "character" | "background"; style?: { text: string; id?: string }; onBack: () => void; onUse: (id: string) => void }) {
  const place = kind === "background";
  const [it, their] = place ? ["it", "Its"] : ["them", "Their"];
  const asset = useAsset(assetId);
  const saveError = useSaveError();
  const styles = useStyles();
  const since = useJob(lookJob(assetId));
  const painting = since !== null;
  const [name, setName] = useState("");
  const [words, setWords] = useState("");
  const [facing, setFacing] = useState(false); // the face placer is open
  const [redo, setRedo] = useState(false); // back on the words after "Try again"
  const [using, setUsing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const here = useRef(true);
  useEffect(() => { here.current = true; return () => { here.current = false; }; }, []);
  if (!asset) return null;
  const has = hasPicture(asset);
  const showMade = has && !redo && !painting;

  // A draft nobody has drawn is thrown away. One being drawn stays until the picture lands.
  function close() {
    if (getDraft(assetId) && !has && !isRunning(lookJob(assetId))) dropDraft(assetId);
    onBack();
  }

  function make() {
    const style = given ?? styleById(styles, lastStyleId());
    if (!given) rememberStyle(style.id!);
    changeAsset(assetId, { name: name.trim() || asset!.name });
    setRedo(false);
    setProblem(null);
    runJob(lookJob(assetId), paintLook(assetId, { how: "drawn", text: words.trim(), refs: [], style: style.text, styleId: style.id }))
      .catch(e => {
        const m = paintProblem((e as Error).message);
        if (!here.current) return notify(`${name.trim() || (place ? "Your place" : "Your character")}: ${m}`);
        setProblem(m);
        if (hasPicture(latestAsset(assetId) ?? asset!)) setRedo(true); // a new try that failed: back to the words, not the old drawing
      });
  }
  function tryAgain() {
    setName(n => n || asset!.name);
    setWords(w => w || asset!.description);
    setRedo(true);
  }

  // They are in the library once saved; wait for that so the cast picker can show them.
  async function use() {
    setUsing(true);
    await saveNow(assetId);
    setUsing(false);
    if (!findAsset(getLibrary(), assetId)) return setToast(`Couldn't save ${it}. Tap Use ${it} again.`);
    onUse(assetId);
  }

  if (facing && !place) {
    return (
      <Layer>
        <Page className="sm">
          <TopBar onBack={() => setFacing(false)} title="Move the face" sub={asset.name} />
          <Body><FaceStage asset={asset} onChange={p => changeAsset(assetId, p)} /></Body>
          <DoneFooter onDone={() => setFacing(false)} />
          {saveError && <Toast>{saveError}</Toast>}
        </Page>
      </Layer>
    );
  }

  return (
    <Layer>
      <Page className="sm">
        <TopBar onBack={redo ? () => setRedo(false) : close} title={place ? "New place" : "New character"} />
        <Body className="sm-stage" page={showMade ? "made" : "form"}>
          {showMade ? (
            place ? null : <button className="link-btn sm-fix" onClick={() => setFacing(true)}>Face in the wrong place?</button>
          ) : (
            <input className="sm-name" value={name} maxLength={40} aria-label="Name" placeholder={`${their} name`} autoFocus={!painting}
              disabled={painting} onChange={e => setName(e.target.value)} />
          )}
          <Panel corners={!painting && !showMade}>
            {painting ? <Drawing message={`Drawing ${name.trim() || it}…`} since={since} />
              : showMade ? <AssetView asset={asset} id="sm-char" />
              : (
                <>
                  <textarea className="sm-write" value={words} aria-label={place ? "What it looks like" : "What they look like"}
                    placeholder={place ? "What does it look like? e.g. A cramped kitchen with mustard walls and a wonky table" : "What do they look like? e.g. A nervous ostrich in a tracksuit, far too tall for doorways"}
                    onChange={e => { setWords(e.target.value); setProblem(null); }} />
                  {problem && <Problem>{problem}</Problem>}
                </>
              )}
          </Panel>
        </Body>
        {!painting && (showMade ? (
          <Footer>
            <button className="btn big" onClick={tryAgain}><Icon name="edit" /> Try again</button>
            <button className="btn big yellow" disabled={using} onClick={() => void use()}><Icon name="check" /> Use {it}</button>
          </Footer>
        ) : (
          <Footer><button className="btn big yellow wide" disabled={!name.trim() || !words.trim()} onClick={make}><Icon name="sparkle" /> {place ? "Draw it" : "Draw them"}</button></Footer>
        ))}
        {(toast || saveError) && <Toast onClick={() => setToast(null)}>{toast ?? saveError}</Toast>}
      </Page>
    </Layer>
  );
}
